interface VideoThumbnailOptions {
    width?: number;
    height?: number;
    quality?: number;
    timeOffset?: number;
}

/**
 * @returns Promise that resolves to a blob URL of the thumbnail image
 */
export const generateVideoThumbnail = async (
    videoUrl: string,
    options: VideoThumbnailOptions = {}
): Promise<string> => {
    const {
        width = 320,
        height = 320,
        quality = 0.8,
        timeOffset = 0.1
    } = options;

    return new Promise((resolve, reject) => {
        const video = document.createElement('video');
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        if (!ctx) {
            reject(new Error('Could not get canvas context'));
            return;
        }

        video.crossOrigin = 'anonymous';
        video.preload = 'metadata';

        video.onloadedmetadata = () => {
            canvas.width = width;
            canvas.height = height;

            video.currentTime = Math.min(timeOffset, video.duration);
        };

        video.onseeked = () => {
            try {
                // Calculate dimensions to maintain aspect ratio
                const videoAspect = video.videoWidth / video.videoHeight;
                const canvasAspect = width / height;

                let drawWidth = width;
                let drawHeight = height;
                let offsetX = 0;
                let offsetY = 0;

                if (videoAspect > canvasAspect) {
                    // Video is wider, fit by height
                    drawHeight = height;
                    drawWidth = height * videoAspect;
                    offsetX = (width - drawWidth) / 2;
                } else {
                    // Video is taller, fit by width
                    drawWidth = width;
                    drawHeight = width / videoAspect;
                    offsetY = (height - drawHeight) / 2;
                }

                ctx.clearRect(0, 0, width, height);
                ctx.fillStyle = '#000000';
                ctx.fillRect(0, 0, width, height);
                ctx.drawImage(video, offsetX, offsetY, drawWidth, drawHeight);

                canvas.toBlob(
                    (blob) => {
                        if (blob) {
                            const thumbnailUrl = URL.createObjectURL(blob);
                            resolve(thumbnailUrl);
                        } else {
                            reject(new Error('Failed to create thumbnail blob'));
                        }
                    },
                    'image/jpeg',
                    quality
                );
            } catch (error) {
                reject(error);
            }
        };

        video.onerror = () => {
            reject(new Error('Failed to load video for thumbnail generation'));
        };

        video.src = videoUrl;
    });
};
